import { useCallback } from 'react';

import {
  getDay,
  getHours,
  isAfter,
  isSameDay,
  startOfDay,
  startOfHour
} from 'date-fns';

import { MAX_HOUR, MIN_HOUR } from '../config/calendarConfig';
import { isOutOfSelectableRange } from '../utils/slotSelectableRange';

const isWeekendDay = (date: Date) => {
  const d = getDay(date);
  return d === 0 || d === 6;
};

interface UseSlotValidationProps {
  maxSelectableDate: Date;
  /** When set, Sat/Sun use disabled styling only in month view (week/day columns stay unchanged). */
  shadeWeekendsOnlyInMonthView?: boolean;
}

export const useSlotValidation = ({
  maxSelectableDate,
  shadeWeekendsOnlyInMonthView = false
}: UseSlotValidationProps) => {
  const isSlotDisabled = useCallback(
    (date: Date) => {
      const now = new Date();
      const slotStart = startOfHour(date);
      const hour = getHours(date);

      if (isOutOfSelectableRange(date, now, maxSelectableDate)) {
        return true;
      }

      if (isWeekendDay(date)) {
        return true;
      }

      // Check if the time is within the allowed range
      if (hour < MIN_HOUR || hour >= MAX_HOUR) {
        return true;
      }

      // If today's day
      if (isSameDay(date, now)) {
        // Block if the hour has already started or passed
        return !isAfter(slotStart, startOfHour(now));
      }

      return false;
    },
    [maxSelectableDate]
  );

  const dayPropGetter = useCallback(
    (date: Date) => {
      const now = new Date();
      const dayStart = startOfDay(date);

      if (isOutOfSelectableRange(dayStart, now, maxSelectableDate)) {
        return {
          className: 'rbc-slot-disabled'
        };
      }

      if (shadeWeekendsOnlyInMonthView && isWeekendDay(dayStart)) {
        return {
          className: 'rbc-slot-disabled'
        };
      }

      return {};
    },
    [maxSelectableDate, shadeWeekendsOnlyInMonthView]
  );

  const slotPropGetter = useCallback(
    (date: Date) => {
      if (isSlotDisabled(date)) {
        return {
          className: 'rbc-slot-disabled'
        };
      }
      return {};
    },
    [isSlotDisabled]
  );

  return {
    isSlotDisabled,
    dayPropGetter,
    slotPropGetter
  };
};
